import { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  IconButton,
  Typography,
  Paper,
  Chip,
  Divider,
  Autocomplete,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import {
  StructuredQuestion,
  QUESTION_TYPES,
  CONDITIONAL_OPPERATORS,
} from '../interfaces/questions'

interface QuestionEditorProps {
  question: StructuredQuestion
  allQuestions: StructuredQuestion[]
  onChange: (updated: StructuredQuestion) => void
  onDelete: () => void
  isDragging?: boolean
  hideDelete?: boolean
}

export default function QuestionEditor({
  question,
  allQuestions,
  onChange,
  onDelete,
  isDragging = false,
  hideDelete = false,
}: QuestionEditorProps) {
  const [localQuestion, setLocalQuestion] =
    useState<StructuredQuestion>(question)
  const [optionInput, setOptionInput] = useState('')
  const [showConditional, setShowConditional] = useState(
    !!question.conditional_on?.question_number,
  )
  const [showDependency, setShowDependency] = useState(
    !!question.depends_on?.question_numbers,
  )

  useEffect(() => {
    setLocalQuestion(question)
  }, [question])

  useEffect(() => {
    setLocalQuestion(prev => {
      if (showConditional && prev.conditional_on && !prev.conditional_on.operator) {
        const updated = {
          ...prev,
          conditional_on: { ...prev.conditional_on, operator: 'equals' },
        }
        onChange(updated)
        return updated
      }
      return prev
    })
  }, [showConditional, localQuestion.conditional_on?.question_number, onChange])

  const handleUpdate = (updates: Partial<StructuredQuestion>) => {
    const updated = { ...localQuestion, ...updates }
    setLocalQuestion(updated)
    onChange(updated)
  }

  const handleAddOption = () => {
    if (!optionInput.trim()) return
    const newOptions = [...(localQuestion.options || []), optionInput.trim()]
    handleUpdate({ options: newOptions })
    setOptionInput('')
  }

  const handleRemoveOption = (index: number) => {
    const newOptions =
      localQuestion.options?.filter((_, i) => i !== index) || []
    handleUpdate({ options: newOptions })
  }

  const handleRemoveConditional = () => {
    handleUpdate({ conditional_on: undefined })
    setShowConditional(false)
  }

  const handleRemoveDependency = () => {
    handleUpdate({ depends_on: undefined })
    setShowDependency(false)
  }

  const isChoiceType =
    localQuestion.question_type === 'single_choice' ||
    localQuestion.question_type === 'multi_choice'

  // Extract unique sections from all questions for autocomplete
  const existingSections = Array.from(
    new Set(
      allQuestions
        .map((q) => q.section)
        .filter((section): section is string => !!section),
    ),
  ).sort()

  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        border: isDragging ? '2px dashed #1976d2' : '1px solid #e0e0e0',
        opacity: isDragging ? 0.5 : 1,
        maxWidth: '100%',
        overflowX: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', gap: 2, my: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Question Number"
              value={localQuestion.question_number}
              onChange={(e) =>
                handleUpdate({ question_number: e.target.value })
              }
              size="small"
              sx={{ width: '150px' }}
              helperText="e.g., 1, 2a, 5c"
            />
            <FormControl size="small" sx={{ width: '200px' }}>
              <InputLabel>Question Type</InputLabel>
              <Select
                value={localQuestion.question_type}
                label="Question Type"
                onChange={(e) =>
                  handleUpdate({ question_type: e.target.value })
                }
              >
                {QUESTION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              freeSolo
              options={existingSections}
              value={localQuestion.section || ''}
              onChange={(_, newValue) =>
                handleUpdate({ section: newValue || '' })
              }
              onInputChange={(_, newInputValue) =>
                handleUpdate({ section: newInputValue })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Section (Optional)"
                  size="small"
                  helperText="Group related questions"
                />
              )}
              sx={{ flex: 1, minWidth: '200px' }}
            />
          </Box>

          {/* Question Text */}
          <TextField
            label="Question"
            value={localQuestion.question}
            onChange={(e) => handleUpdate({ question: e.target.value })}
            fullWidth
            multiline
            rows={2}
            sx={{ mb: 2 }}
            required
          />

          {/* Question Details */}
          <TextField
            label="Question Details (Optional)"
            value={localQuestion.question_details || ''}
            onChange={(e) => handleUpdate({ question_details: e.target.value })}
            fullWidth
            multiline
            rows={2}
            sx={{ mb: 2 }}
            helperText="Additional context or instructions"
          />

          {/* Options for choice questions */}
          {isChoiceType && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Options (minimum 2 required )
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  mb: 1,
                  flexWrap: 'wrap',
                }}
              >
                {localQuestion.options?.map((option, index) => (
                  <Chip
                    key={index}
                    label={option}
                    onDelete={() => handleRemoveOption(index)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  value={optionInput}
                  onChange={(e) => setOptionInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddOption()
                    }
                  }}
                  placeholder="Add option"
                  size="small"
                  fullWidth
                />
                <Button
                  type="button"
                  onClick={handleAddOption}
                  startIcon={<AddIcon />}
                  variant="outlined"
                >
                  Add
                </Button>
              </Box>
            </Box>
          )}

          {/* Limits */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Character Limit"
              type="number"
              value={localQuestion.char_limit || ''}
              onChange={(e) =>
                handleUpdate({
                  char_limit: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                })
              }
              size="small"
              sx={{ width: '150px' }}
              helperText="Optional"
            />
            <TextField
              label="Word Limit"
              type="number"
              value={localQuestion.word_limit || ''}
              onChange={(e) =>
                handleUpdate({
                  word_limit: e.target.value
                    ? parseInt(e.target.value)
                    : undefined,
                })
              }
              size="small"
              sx={{ width: '150px' }}
              helperText="Optional"
            />
          </Box>

          {/* Flags */}
          <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localQuestion.required}
                  onChange={(e) => handleUpdate({ required: e.target.checked })}
                />
              }
              label="Required"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={localQuestion.attachment_required}
                  onChange={(e) =>
                    handleUpdate({ attachment_required: e.target.checked })
                  }
                />
              }
              label="Attachment Required"
            />
          </Box>

          {/* Show for choice questions */}
          {isChoiceType && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={localQuestion.has_other_text_field || false}
                  onChange={(e) =>
                    handleUpdate({ has_other_text_field: e.target.checked })
                  }
                />
              }
              label='Enable "Other" Text Input'
            />
          )}

          <Divider sx={{ my: 2 }} />

          {/* Conditional Logic */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Typography variant="subtitle2" color="text.primary">
              Conditional Logic & Dependencies
            </Typography>

            {!showConditional && !showDependency && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  type="button"
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => setShowConditional(true)}
                >
                  Add Conditional
                </Button>
                <Button
                  type="button"
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => setShowDependency(true)}
                >
                  Add Dependency
                </Button>
              </Box>
            )}

            {/* Conditional Display Section */}
            {showConditional && (
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2">
                    Conditional Display
                  </Typography>
                  <Button
                    type="button"
                    size="small"
                    color="error"
                    onClick={handleRemoveConditional}
                  >
                    Remove
                  </Button>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 2, display: 'block' }}
                >
                  Only show this question when another question meets certain
                  criteria
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: '250px', flex: 1 }}>
                    <InputLabel>Based on Question</InputLabel>
                    <Select
                      value={
                        localQuestion.conditional_on?.question_number || ''
                      }
                      label="Based on Question"
                      onChange={(e) =>
                        handleUpdate({
                          conditional_on: {
                            ...localQuestion.conditional_on,
                            question_number: e.target.value,
                          },
                        })
                      }
                    >
                      {allQuestions
                        .filter((q) => q.question_number !== localQuestion.question_number)
                        .map((q) => (
                          <MenuItem key={q.index} value={q.question_number}>
                            {q.question_number} - {q.question.substring(0, 50)}
                            {q.question.length > 50 ? '...' : ''}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: '150px' }}>
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={localQuestion.conditional_on?.operator || 'equals'}
                      label="Operator"
                      onChange={(e) =>
                        handleUpdate({
                          conditional_on: {
                            ...localQuestion.conditional_on,
                            operator: e.target.value,
                          },
                        })
                      }
                    >
                      {CONDITIONAL_OPPERATORS.map((op) => (
                        <MenuItem key={op.value} value={op.value}>
                          {op.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Value"
                    value={localQuestion.conditional_on?.value || ''}
                    onChange={(e) =>
                      handleUpdate({
                        conditional_on: {
                          ...localQuestion.conditional_on,
                          value: e.target.value,
                        },
                      })
                    }
                    size="small"
                    sx={{ minWidth: '200px', flex: 1 }}
                    helperText="Expected answer value"
                  />
                </Box>
              </Paper>
            )}

            {/* Dependency Section */}
            {showDependency && (
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle2">
                    Question Dependency
                  </Typography>
                  <Button
                    type="button"
                    size="small"
                    color="error"
                    onClick={handleRemoveDependency}
                  >
                    Remove
                  </Button>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 2, display: 'block' }}
                >
                  This question relates to or builds upon other questions
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Related Questions</InputLabel>
                    <Select
                      multiple
                      value={localQuestion.depends_on?.question_numbers || []}
                      label="Related Questions"
                      onChange={(e) =>
                        handleUpdate({
                          depends_on: {
                            ...localQuestion.depends_on,
                            question_numbers:
                              typeof e.target.value === 'string'
                                ? [e.target.value]
                                : e.target.value,
                          },
                        })
                      }
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {allQuestions
                        .filter((q) => q.question_number !== localQuestion.question_number)
                        .map((q) => (
                          <MenuItem key={q.index} value={q.question_number}>
                            {q.question_number} - {q.question.substring(0, 50)}
                            {q.question.length > 50 ? '...' : ''}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>
              </Paper>
            )}
          </Box>
        </Box>

        {/* Delete button */}
        {!hideDelete && (
          <IconButton
            type="button"
            onClick={onDelete}
            color="error"
            size="small"
            sx={{ mt: 1 }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>
    </Paper>
  )
}
